import { pdfjs } from 'react-pdf';
import { v4 as uuidv4 } from 'uuid';
import { CONSENT_ELEMENT_TYPES } from '../constants/consent_element_types.constants';
// Initialize pdfjs worker once at module load
const pdfjsBaseUrl = `//unpkg.com/pdfjs-dist@${pdfjs.version}`;
pdfjs.GlobalWorkerOptions.workerSrc = `${pdfjsBaseUrl}/build/pdf.worker.min.mjs`;
function clampPct(v) {
    return Math.max(0, Math.min(1, v));
}
// PDF coordinates are in "points" (1 point = 1/72 inch), so even a small
// position like 0.5 inches = 36 points. Normalized fractions are always 0–1,
// so a threshold of ~1 cleanly separates the two coordinate systems.
function looksLikeNormalizedFractions(fields) {
    return fields.every((f) => Math.max(f.x, f.y, f.width, f.height) <= 1.005);
}
/**
 * Loads PDF page dimensions (scale 1) keyed by 1-based page index — matches `react-pdf` Page at scale 1.
 */
export async function loadPdfPageSizeMap(pdfDataUrl, maxPage) {
    const loadingTask = pdfjs.getDocument(pdfDataUrl);
    const pdf = await loadingTask.promise;
    const map = new Map();
    const last = Math.min(maxPage, pdf.numPages);
    for (let p = 1; p <= last; p++) {
        const page = await pdf.getPage(p);
        const vp = page.getViewport({ scale: 1 });
        map.set(p, { width: vp.width, height: vp.height });
    }
    return map;
}
/**
 * Maps agent `fields` to editor `ConsentElement` markers.
 * - If all fields look like 0–1 fractions, treats them as DOM-style (top-left origin) percentages.
 * - Otherwise treats x,y,width,height as PDF user-space units (origin bottom-left) at scale 1 and converts using page viewports.
 */
export async function mapAgentSignatureFieldsToConsentElements(fields, pdfDataUrl) {
    if (fields.length === 0) {
        return [];
    }
    const maxPage = Math.max(...fields.map((f) => f.page));
    const pageSizes = await loadPdfPageSizeMap(pdfDataUrl, maxPage);
    const def = CONSENT_ELEMENT_TYPES.SIGNATURE;
    const useNormalized = looksLikeNormalizedFractions(fields);
    return fields.map((f, index) => {
        let xPct;
        let yPct;
        let widthPct;
        let heightPct;
        if (useNormalized) {
            xPct = clampPct(f.x);
            yPct = clampPct(f.y);
            widthPct = clampPct(f.width);
            heightPct = clampPct(f.height);
            if (xPct + widthPct > 1) {
                widthPct = clampPct(1 - xPct);
            }
            if (yPct + heightPct > 1) {
                heightPct = clampPct(1 - yPct);
            }
        }
        else {
            const dims = pageSizes.get(f.page);
            if (!dims) {
                const fallbackW = 612;
                const fallbackH = 792;
                ({ widthPct: widthPct, heightPct: heightPct, xPct, yPct } = fromPdfPoints(f, fallbackW, fallbackH));
            }
            else {
                ({ widthPct: widthPct, heightPct: heightPct, xPct, yPct } = fromPdfPoints(f, dims.width, dims.height));
            }
        }
        // sets minimum width and height so fields aren't too small and can still be easily resized
        if (widthPct < def.defaultWidth * 0.25) {
            widthPct = def.defaultWidth;
        }
        if (heightPct < def.defaultHeight * 0.25) {
            heightPct = def.defaultHeight;
        }
        // clamps xPct and yPct to ensure the field is within the page
        if (xPct + widthPct > 1) {
            xPct = clampPct(1 - widthPct);
        }
        if (yPct + heightPct > 1) {
            yPct = clampPct(1 - heightPct);
        }
        const id = `el-agent-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 9)}`;
        return {
            id,
            type: 'SIGNATURE',
            page: f.page,
            position: { xPct, yPct, widthPct, heightPct },
            config: { ec__key: uuidv4(), ec__label: f.label || undefined },
        };
    });
}
function fromPdfPoints(f, pageWidth, pageHeight) {
    const xPct = clampPct(f.x / pageWidth);
    const widthPct = clampPct(f.width / pageWidth);
    // PDF user space: origin bottom-left; DOM overlay: origin top-left
    const yPct = clampPct((pageHeight - f.y - f.height) / pageHeight);
    const heightPct = clampPct(f.height / pageHeight);
    return { xPct, yPct, widthPct, heightPct };
}
